// dependency-graph-ignore-file

import { contextualMenuState } from '@databases/contextual-menu/contextual-menu-state';
import { type BridgeData } from '@databases/database-state/state/bridge-data';
import { setAppMode } from '@databases/modes';
import { container } from '@databases/service-container';
import { captureException } from '@sentry/core';

import { maybeParseConfluenceDatabasesBridgeClientParams } from '@canvas/databases-bridge-api';
import {
  DatabaseUIReadyCommand,
  DismissContextMenuCommand,
  EnabledFeaturesCommand,
  OnShowContextMenuCommand,
} from '@canvas/databases-bridge-api/commands';
import { mergeEnabledFeatures } from '@canvas/databases-bridge-api/utils';
import { client, getBrowserBridgeAPI } from '@canvas/rovo-content-bridge-api';

import { mapBridgeCommandsToLocalMethods } from './map-bridge-commands-to-local-methods';
import { sendInitialBridgeCommands } from './send-initial-bridge-commands';
import { setupEventListeners } from './setup-event-listeners';
import { getLocalEnabledFeatureList } from './utils/get-local-enabled-feature-list';

const DEFAULT_HANDSHAKE_TIMEOUT_MS = 5000;

const withTimeout = <T>(value: PromiseLike<T> | T, ms: number) =>
  new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('Bridge handshake timeout')), ms);

    Promise.resolve(value)
      .then((v) => {
        clearTimeout(t);
        resolve(v);
      })
      .catch((e) => {
        clearTimeout(t);
        reject(e);
      });
  });

/**
 * When there is an issue establishing the bridge connection, fallback to EMBED mode.
 * In local environments, we skip the fallback to avoid switching to embed mode.
 */
function defaultOnFallback(e?: any) {
  const hostname =
    typeof globalThis !== 'undefined' && globalThis.location
      ? globalThis.location.hostname
      : '';
  const isLocal = hostname === 'canvas.localhost' || hostname === 'databases.localhost';

  if (isLocal) {
    return;
  }

  setAppMode('EMBED');

  captureException(
    e ?? new Error('Failed to establish Native Embed Bridge. Falling back to EMBED.'),
  );
}

/**
 * Attempts to initialize a native embed bridge for communication between local and external clients.
 *
 * This function establishes a bridge connection in native embed mode by:
 * 1. Parsing bridge parameters from the URL
 * 2. Performing a handshake between local and external clients
 * 3. Merging feature lists from both clients
 * 4. Mapping bridge commands to local methods
 *
 * @param getBridgeData - Function that returns bridge data needed for initialization
 * @param options - Optional configuration object
 * @param options.onFallback - Callback function to execute when bridge initialization fails or falls back
 * @param options.handshakeTimeoutMs - Timeout in milliseconds for the handshake process
 *
 * @returns A cleanup function to destroy the bridge if initialization succeeds, otherwise undefined
 *
 * @throws Will call the onFallback function if any errors occur during initialization
 *
 * @example
 * ```typescript
 * const cleanup = await tryInitializeNativeEmbedBridge(
 *   () => currentBridgeData,
 *   {
 *     onFallback: (error) => console.error('Bridge init failed:', error),
 *     handshakeTimeoutMs: 5000
 *   }
 * );
 *
 * // Later, cleanup the bridge
 * cleanup?.();
 * ```
 */
export async function tryInitializeNativeEmbedBridge(
  getBridgeData: () => BridgeData,
  options?: { onFallback?: (e?: any) => void; handshakeTimeoutMs?: number },
) {
  const onFallback = options?.onFallback ?? defaultOnFallback;

  const params = maybeParseConfluenceDatabasesBridgeClientParams(window.location.search);

  /**
   * If the bridge params have not been properly set or are omitted, there is no proper way
   * to establish communication between a local and external client, so we return.
   */
  if (!params) {
    // Fallback: unable to initialize native embed without bridge params
    onFallback();

    return;
  }

  try {
    const localClient = client(params.localClientId);
    const externalClient = client(params.externalClientId);
    const bridge = getBrowserBridgeAPI();
    const localFeatureList = getLocalEnabledFeatureList();

    bridge.ready(localClient);

    /**
     * To initiate a "handshake" with the external client, the local client
     * sends over a list of currently enabled features and waits for one in return.
     */
    const command = bridge.send(EnabledFeaturesCommand, {
      features: localFeatureList,
      senderId: localClient,
      receiverId: externalClient,
    });

    const externalFeaturesPromise = command.result();
    const externalFeatures = await withTimeout(
      externalFeaturesPromise,
      options?.handshakeTimeoutMs ?? DEFAULT_HANDSHAKE_TIMEOUT_MS,
    );

    /**
     * If the external client doesn't send us a list of enabled features then we ignore
     * and don't initialize the bridge mapping.
     */
    if (!externalFeatures) {
      // Fallback: handshake did not return features; continue in embed mode

      onFallback();

      return;
    }

    /**
     * The local and external features list is merged to determine what features they both support.
     */
    const mergedFeatureList = mergeEnabledFeatures(localFeatureList, externalFeatures);
    const clients = {
      localClientId: localClient,
      externalClientId: externalClient,
    };

    const cleanupLocalHandlers = mapBridgeCommandsToLocalMethods(bridge, {
      getBridgeData,
      featureList: mergedFeatureList,
      clients,
    });

    const cleanupEventListeners = setupEventListeners();

    /**
     * Set up a listener for the database UI loaded event.
     * When the database UI is loaded, we send a command to the external client
     * to notify it that the database UI is ready.
     */
    const eventBus = container.resolve('EventBus');
    eventBus.on('databaseUILoaded', () => {
      bridge.send(DatabaseUIReadyCommand, {
        senderId: localClient,
        receiverId: externalClient,
      });
    });

    /**
     * Set up a listener for when Databases shows a context menu.
     * When a context menu is shown, we send a command to the external client
     * (Whiteboards) to dismiss its context menu.
     */
    eventBus.on('databaseContextMenuShown', () => {
      bridge.send(OnShowContextMenuCommand, {
        senderId: localClient,
        receiverId: externalClient,
      });
    });

    bridge.on(localClient, DismissContextMenuCommand, () => {
      contextualMenuState.closeMenu();
    });

    sendInitialBridgeCommands(bridge, {
      getBridgeData,
      featureList: mergedFeatureList,
      clients,
    });

    return () => {
      bridge.destroy();
      cleanupLocalHandlers();
      cleanupEventListeners();
    };
  } catch (e) {
    onFallback(e);
  }
}
